//@ts-nocheck
import React, { useState, useRef } from 'react';

interface SkillsInputProps {
  value: string[];
  onChange: (skills: string[]) => void;
  maxSkills?: number;
  required?: boolean;
  error?: string;
}

const SkillsInput: React.FC<SkillsInputProps> = ({
  value = [],
  onChange,
  maxSkills = 50,
  className = "",
  required = false,
  error
}) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle Enter key or comma
    if ((e.key === 'Enter' || e.key === ',') && inputValue.trim()) {
      e.preventDefault();
      addSkill();
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      // Remove last skill when backspace is pressed and input is empty
      removeSkill(value.length - 1);
    }
  };

  const addSkill = () => {
    const trimmedInput = inputValue.trim();
    
    // Skip if empty, already exists, or max skills reached
    if (!trimmedInput || value.includes(trimmedInput) || value.length >= maxSkills) {
      setInputValue('');
      return;
    }
    
    const updatedSkills = [...value, trimmedInput];
    onChange(updatedSkills);
    setInputValue('');
  };

  const removeSkill = (index: number) => {
    const updatedSkills = [...value];
    updatedSkills.splice(index, 1);
    onChange(updatedSkills);
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addSkill();
    }
  };

  const focusInput = () => {
    inputRef.current?.focus();
  };

  return (
    <div className="space-y-2">
      <label  className="wg-label capitalize inline-flex shrink-0 items-center gap-1 text-[13px] md:text-[14px] lg:text-[14px] text-[#2f2f31] wg-antialiased leading-4 md:leading-5 lg:leading-5 font-medium">
        Skills
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      <div 
        className={`flex flex-wrap items-center gap-2  min-h-[42px] ${error ? 'border-red-500' : 'focus-within:border-indigo-500'}`}
        onClick={focusInput}
      >
        {value.map((skill, index) => (
          <div 
            key={index} 
            className="flex items-start bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded-md"
          >
            <span>{skill}</span>
            <button
              type="button"
              className="ml-1 text-blue-600 hover:text-blue-800 focus:outline-none"
              onClick={(e) => {
                e.stopPropagation();
                removeSkill(index);
              }}
            >
              &times;
            </button>
          </div>
        ))}
        
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={value.length === 0 ? "Add skills (e.g., React, JavaScript, UI Design)" : ""}
          className={`
            block w-full rounded-md bg-white px-3 py-2 relative
            text-[14px] text-gray-900 
            outline outline-0 -outline-offset-2
            ${error ? 'outline-red-500 focus:ring-0 border-red-500' : 'border-[#d1d5db] outline-gray-300'}
            placeholder:text-gray-400
            focus:outline focus:outline-2 focus:-outline-offset-2 
            ${error ? 'focus:outline-red-500 focus:ring-0' : 'input_hover'}
            sm:text-sm/6
            ${className}
          `}
          disabled={value.length >= maxSkills}
        />
      </div>

      {value.length >= maxSkills && (
        <p className="text-xs text-amber-600">
          Maximum of {maxSkills} skills reached
        </p>
      )}

      {error && (
        <p className="text-xs text-red-500">
          {error}
        </p>
      )}

      <p className="text-xs text-gray-500">
        Press Enter or comma to add a skill. You can add up to {maxSkills} skills.
      </p>
    </div>
  );
};



export default SkillsInput;